import React, { useState } from 'react';
import { useInput } from 'react-admin';

import {
  Box,
  Button,
  IconButton,
  Popover,
  Tooltip,
  Typography,
} from '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import CloseIcon from '@material-ui/icons/Close';

const NOMES_MES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

const MESES_CURTOS = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez',
];

const doMes = (ano, mes) => `${ano}-${String(mes).padStart(2, '0')}`;

// Valor no formato AAAA-MM (mensal) ou AAAA (anual); vazio = todas as competências.
function deslocar(valor, delta) {
  if (valor && valor.length === 4) return String(Number(valor) + delta);
  const base = valor
    ? new Date(Number(valor.slice(0, 4)), Number(valor.slice(5, 7)) - 1, 1)
    : new Date();
  const data = new Date(base.getFullYear(), base.getMonth() + delta, 1);
  return doMes(data.getFullYear(), data.getMonth() + 1);
}

function rotulo(valor) {
  if (!valor) return 'Todas';
  if (valor.length === 4) return `Ano de ${valor}`;
  return `${NOMES_MES[Number(valor.slice(5, 7)) - 1]} de ${valor.slice(0, 4)}`;
}

// permiteAnual: oferece também a competência anual (AAAA), usada por modelos anuais.
const CompetenciaInput = ({
  label = 'Competência',
  permiteAnual = false,
  ...props
}) => {
  const {
    input: { name, value, onChange },
  } = useInput(props);
  const [ancora, setAncora] = useState(null);
  const hoje = new Date();
  const [anoVisto, setAnoVisto] = useState(hoje.getFullYear());

  function alterar(novo) {
    onChange({ target: { name, value: novo } });
  }

  function abrir(evento) {
    setAnoVisto(value ? Number(value.slice(0, 4)) : hoje.getFullYear());
    setAncora(evento.currentTarget);
  }

  function escolher(novo) {
    alterar(novo);
    setAncora(null);
  }

  return (
    <Box
      display="inline-flex"
      flexDirection="column"
      style={{ margin: '8px 0 4px' }}
    >
      <Typography
        variant="caption"
        color="textSecondary"
        style={{ marginLeft: 4 }}
      >
        {label}
      </Typography>
      <Box
        display="flex"
        alignItems="center"
        style={{
          border: '1px solid #bdbdbd',
          borderRadius: 20,
          padding: '0 4px',
          height: 40,
        }}
      >
        <IconButton size="small" onClick={() => alterar(deslocar(value, -1))}>
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <Tooltip title="Escolher mês ou ano">
          <Button
            size="small"
            onClick={abrir}
            style={{
              minWidth: 130,
              textTransform: 'none',
              fontWeight: value ? 600 : 400,
              color: value ? 'inherit' : '#9e9e9e',
            }}
          >
            {rotulo(value)}
          </Button>
        </Tooltip>
        <IconButton size="small" onClick={() => alterar(deslocar(value, 1))}>
          <ChevronRightIcon fontSize="small" />
        </IconButton>
        {value ? (
          <Tooltip title="Limpar">
            <IconButton size="small" onClick={() => alterar('')}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
      </Box>
      <Popover
        open={Boolean(ancora)}
        anchorEl={ancora}
        onClose={() => setAncora(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Box p={1.5} width={260}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <IconButton size="small" onClick={() => setAnoVisto(anoVisto - 1)}>
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              {anoVisto}
            </Typography>
            <IconButton size="small" onClick={() => setAnoVisto(anoVisto + 1)}>
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box
            display="grid"
            mt={1}
            style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}
          >
            {MESES_CURTOS.map((mes, indice) => {
              const valorMes = doMes(anoVisto, indice + 1);
              const atual =
                anoVisto === hoje.getFullYear() && indice === hoje.getMonth();
              return (
                <Button
                  key={mes}
                  size="small"
                  color="primary"
                  variant={value === valorMes ? 'contained' : 'outlined'}
                  style={{
                    textTransform: 'none',
                    borderStyle:
                      atual && value !== valorMes ? 'dashed' : undefined,
                  }}
                  onClick={() => escolher(valorMes)}
                >
                  {mes}
                </Button>
              );
            })}
          </Box>
          <Box display="flex" justifyContent="space-between" mt={1.5}>
            <Button
              size="small"
              onClick={() =>
                escolher(doMes(hoje.getFullYear(), hoje.getMonth() + 1))
              }
            >
              Mês atual
            </Button>
            {permiteAnual && (
              <Button
                size="small"
                color="primary"
                variant={value === String(anoVisto) ? 'contained' : 'text'}
                onClick={() => escolher(String(anoVisto))}
              >
                {`Ano ${anoVisto}`}
              </Button>
            )}
            <Button size="small" onClick={() => escolher('')}>
              Limpar
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default CompetenciaInput;
